import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI, useTRPCMutation } from '../../../trpc/trpc';

export interface UseFollowPlaylistOptions {
  onSuccess?: (playlistId: string) => void;
  onError?: (error: Error, playlistId: string) => void;
}

export const useFollowPlaylist = (options: UseFollowPlaylistOptions = {}) => {
  const { onSuccess, onError } = options;
  const trpcMusicAPI = useTRPCAPI();
  const queryClient = useQueryClient();

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const currentPlaylistIdRef = useRef<string | null>(null);

  const followMutation = useTRPCMutation(trpcMusicAPI.playlists.follow.mutationOptions());
  const unfollowMutation = useTRPCMutation(trpcMusicAPI.playlists.unfollow.mutationOptions());

  const followPlaylist = useCallback(
    (playlistId: string) => {
      currentPlaylistIdRef.current = playlistId;
      followMutation.mutate(
        { playlistId },
        {
          onSuccess: () => {
            Promise.all([
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.playlists.listFollowed.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.playlists.isFollowing.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.playlists.details.queryKey(),
              }),
            ]).catch(console.error);
            onSuccessRef.current?.(playlistId);
          },
          onError: (error) => {
            const errorObj = error instanceof Error ? error : new Error(error.message);
            onErrorRef.current?.(errorObj, playlistId);
          },
        }
      );
    },
    [
      followMutation,
      queryClient,
      trpcMusicAPI.playlists.listFollowed,
      trpcMusicAPI.playlists.isFollowing,
      trpcMusicAPI.playlists.details,
    ]
  );

  const unfollowPlaylist = useCallback(
    (playlistId: string) => {
      currentPlaylistIdRef.current = playlistId;
      unfollowMutation.mutate(
        { playlistId },
        {
          onSuccess: () => {
            Promise.all([
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.playlists.listFollowed.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.playlists.isFollowing.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.playlists.details.queryKey(),
              }),
            ]).catch(console.error);
            onSuccessRef.current?.(playlistId);
          },
          onError: (error) => {
            const errorObj = error instanceof Error ? error : new Error(error.message);
            onErrorRef.current?.(errorObj, playlistId);
          },
        }
      );
    },
    [
      unfollowMutation,
      queryClient,
      trpcMusicAPI.playlists.listFollowed,
      trpcMusicAPI.playlists.isFollowing,
      trpcMusicAPI.playlists.details,
    ]
  );

  return {
    followPlaylist,
    unfollowPlaylist,
    isFollowPending: followMutation.isPending,
    isUnfollowPending: unfollowMutation.isPending,
    isPending: followMutation.isPending || unfollowMutation.isPending,
  };
};
